import React from "react";
import { useNavigate, Link } from "react-router-dom";
import moment from "moment";
import { IEventSummary, ICourseSummary, IClub } from "@eagerdog/interfaces";

import Chip from "src/components/Chip/Chip";
import Button from "src/components/Button/Button";
import HorizontalScroll from "src/components/HorizontalScroll/HorizontalScroll";
import { helperService } from "src/services/helper.service";

import styles from "./DiscoverCard.module.scss";

import cover1 from "../../../assets/discover_1.jpg";
import cover2 from "../../../assets/discover_2.jpg";
import cover3 from "../../../assets/discover_3.jpg";
import cover4 from "../../../assets/discover_4.jpg";

import profile1 from "../../../assets/discover_c_1.jpg";
import profile2 from "../../../assets/discover_c_2.jpg";
import profile3 from "../../../assets/discover_c_3.jpg";
import profile4 from "../../../assets/discover_c_4.jpg";

const coverImages = [cover1, cover2, cover3, cover4];
const profileImages = [profile1, profile2, profile3, profile4];

interface IProps {
  type: "event" | "course" | "club";
  data: IEventSummary | ICourseSummary | IClub;
  index: number;
}

const DiscoverCard: React.FC<IProps> = (props) => {
  const { type, data, index } = props;
  const navigate = useNavigate();

  // Club card
  if (type === "club") {
    const club = data as IClub;
    const location = club.address
      ? [club.address.city, club.address.state, club.address.country].filter(Boolean).join(", ")
      : "";
    const description = club.description
      ? club.description.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
      : "";
    return (
      <div className={styles.DiscoverCard}>
        <div
          className={styles.imageWrap}
          style={{ backgroundImage: `url('${club.cover_image_url}')` }}
        >
          <div className={styles.chipWrap}>
            <Chip className={styles.discoverChip}>Club</Chip>
          </div>
        </div>
        <div
          className={styles.profileWrap}
          style={{ backgroundImage: `url('${club.logo_url}')` }}
        />
        <div className={styles.content}>
          <span className={styles.title}>{club.club_name}</span>
          {location && <span className={styles.location}>{location}</span>}
          {description && (
            <span className={styles.description}>{description}</span>
          )}
          <div className={styles.actions} style={{ marginTop: "auto" }}>
            <Button className={styles.view} onClick={() => navigate(`/k/${club._id}`)}>
              View Club
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Event / Course card
  const eventOrCourse = data as IEventSummary | ICourseSummary;
  const club = (eventOrCourse as IEventSummary | ICourseSummary).club;
  const coverImage = club?.cover_image_url || coverImages[index % coverImages.length];
  const profileImage = club?.logo_url || profileImages[index % profileImages.length];

  const statusLabel = helperService.getEventStatus(eventOrCourse.status);
  const statusClass = eventOrCourse.status === "published" ? "live" : "upcoming";

  const clubName = eventOrCourse.club_name || club?.club_name || "";
  const clubId   = club?._id || (eventOrCourse as any).club_id || "";
  const location = eventOrCourse.address
    ? [eventOrCourse.address.city, eventOrCourse.address.state, eventOrCourse.address.country].filter(Boolean).join(", ")
    : "";

  const getPreEntry = (): string | null => {
    if (type === "event") {
      const event = eventOrCourse as IEventSummary;
      if (event.registration_end_date) {
        return `Pre-Entry Closes ${moment(event.registration_end_date).format("MM/DD/YYYY")}`;
      }
    } else {
      const course = eventOrCourse as ICourseSummary;
      if (course.early_bird_end_date) {
        return `Early Bird Ends ${moment(course.early_bird_end_date).format("MM/DD/YYYY")}`;
      }
    }
    return null;
  };

  const getShowTypes = (): string[] => {
    if (type === "event") {
      const event = eventOrCourse as IEventSummary;
      const types: string[] = [];
      for (const st of event.show_types || []) {
        if (!types.includes(st.show_type)) types.push(st.show_type);
      }
      return types;
    }
    return [];
  };

  const getSanctioningLabel = (): string => {
    if (type === "event") {
      return (eventOrCourse as IEventSummary).sanctioning_club || "";
    }
    const course = eventOrCourse as ICourseSummary;
    if (course.course_type === "seminar") return "Seminar";
    if (course.course_type === "drop_in") return "Class";
    return "Course";
  };

  const premiumUrl = type === "event" ? (eventOrCourse as IEventSummary).premium_file_url : undefined;
  const showTypes = getShowTypes();
  const preEntry = getPreEntry();
  const sanctioningLabel = getSanctioningLabel();
  const viewLink = type === "event" ? `/e/${eventOrCourse.handle}` : `/c/${eventOrCourse.handle}`;

  return (
    <div className={styles.DiscoverCard}>
      <div className={styles.imageWrap} style={{ backgroundImage: `url('${coverImage}')` }}>
        <div className={styles.dateWrap}>
          <span className={styles.date}>{eventOrCourse.start_date ? moment(eventOrCourse.start_date).format("D") : "--"}</span>
          <span className={styles.month}>{eventOrCourse.start_date ? moment(eventOrCourse.start_date).format("MMM").toUpperCase() : "---"}</span>
        </div>
        <div className={styles.chipWrap}>
          <Chip className={`${styles.discoverChip} ${styles[statusClass]}`}>{statusLabel}</Chip>
          {sanctioningLabel && <Chip className={styles.discoverChip}>{sanctioningLabel}</Chip>}
        </div>
        {premiumUrl && (
          <div className={styles.premiumWrap}>
            <a href={premiumUrl} target="_blank" rel="noreferrer">
              <Chip className={styles.discoverChip}>View Premium</Chip>
            </a>
          </div>
        )}
      </div>
      <div className={styles.profileWrap} style={{ backgroundImage: `url('${profileImage}')` }}></div>
      <div className={styles.content}>
        <span className={styles.title}>{eventOrCourse.title}</span>
        {clubName && (
          clubId
            ? <Link to={`/k/${clubId}`} className={`${styles.club} ${styles.clubLink}`}>{clubName}</Link>
            : <span className={styles.club}>{clubName}</span>
        )}
        {location && <span className={styles.location}>{location}</span>}
        {preEntry && <span className={styles.preEntry}>{preEntry}</span>}
        <div className={styles.horizontalWrap}>
          <HorizontalScroll className={styles.discoverScroll}>
            {showTypes.map((st, i) => (
              <Chip key={i} className={styles.discoverScrollChip}>{st}</Chip>
            ))}
          </HorizontalScroll>
        </div>
        <div className={styles.actions}>
          <Button className={styles.view} onClick={() => navigate(viewLink)}>
            {type === "event" ? "View Event" : "View Course"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DiscoverCard;
